import type { MapCompassTarget, MapDataGameCoordinateDeltas, MapDataGameCoordinates } from '@repo/types/maps/map-types';

const AUTH_PROTOCOL_PREFIX = 'auth.';

/** Carries the token in the `Sec-WebSocket-Protocol` header, which keeps it out of the URL. */
export function toMapPreferencesAuthProtocol(token: string): string {
    return `${AUTH_PROTOCOL_PREFIX}${token}`;
}

export interface PreferencesField {
    value: string;
    updatedAt: number;
}

export const MAX_PREFERENCE_FUTURE_DRIFT_MS = 30_000;

export function normalizePreferenceTimestamp(updatedAt: number, storedAt: number, now = Date.now()): number {
    if (updatedAt <= now + MAX_PREFERENCE_FUTURE_DRIFT_MS) return updatedAt;
    return Math.max(now, storedAt + 1);
}

export type PreferencesRecord = Record<string, PreferencesField>;

export type PreferenceFieldKey =
    | 'selectedMarkerId'
    | 'activeCategoryId'
    | 'sidebarOpen:web'
    | 'sidebarOpen:app'
    | 'sidebarOpen:overlay'
    | 'sidebarOpen:remote'
    | 'hiddenSubcategories'
    | 'hiddenRegions'
    | 'regionFilterMode'
    | 'hideAll'
    | 'teleportOnly'
    | 'unfoundOnly'
    | 'layerVisibility'
    | 'showTeleportWarning'
    | 'trackedCompassPin'
    | 'activeDevelopmentMessageDismissedAt';

export type TrackedCompassPin = MapCompassTarget;

function isGameCoords(value: unknown): value is NonNullable<TrackedCompassPin['gameCoords']> {
    return (
        typeof value === 'object' &&
        value !== null &&
        'x' in value &&
        typeof value.x === 'number' &&
        Number.isFinite(value.x) &&
        'y' in value &&
        typeof value.y === 'number' &&
        Number.isFinite(value.y) &&
        'z' in value &&
        typeof value.z === 'number' &&
        Number.isFinite(value.z)
    );
}

function isGameCoordinateAxis(value: unknown): value is 'x' | 'y' | 'z' {
    return value === 'x' || value === 'y' || value === 'z';
}

function isGameCoordinateDeltas(value: unknown): value is MapDataGameCoordinateDeltas {
    if (typeof value !== 'object' || value === null) return false;
    if (
        !('scaleX' in value) ||
        typeof value.scaleX !== 'number' ||
        !Number.isFinite(value.scaleX) ||
        value.scaleX === 0 ||
        !('scaleY' in value) ||
        typeof value.scaleY !== 'number' ||
        !Number.isFinite(value.scaleY) ||
        value.scaleY === 0 ||
        !('offsetX' in value) ||
        typeof value.offsetX !== 'number' ||
        !Number.isFinite(value.offsetX) ||
        !('offsetY' in value) ||
        typeof value.offsetY !== 'number' ||
        !Number.isFinite(value.offsetY) ||
        !('gameCoordsMap' in value) ||
        typeof value.gameCoordsMap !== 'object' ||
        value.gameCoordsMap === null
    ) {
        return false;
    }

    return (
        'x' in value.gameCoordsMap &&
        isGameCoordinateAxis(value.gameCoordsMap.x) &&
        'y' in value.gameCoordsMap &&
        isGameCoordinateAxis(value.gameCoordsMap.y) &&
        'z' in value.gameCoordsMap &&
        isGameCoordinateAxis(value.gameCoordsMap.z)
    );
}

export function parseTrackedCompassPin(value: string): TrackedCompassPin | null {
    if (!value) return null;

    let parsed: unknown;
    try {
        parsed = JSON.parse(value);
    } catch {
        return null;
    }

    if (
        typeof parsed !== 'object' ||
        parsed === null ||
        !('externalId' in parsed) ||
        typeof parsed.externalId !== 'string' ||
        !('name' in parsed) ||
        typeof parsed.name !== 'string' ||
        !('lngLat' in parsed) ||
        typeof parsed.lngLat !== 'object' ||
        parsed.lngLat === null ||
        !('lng' in parsed.lngLat) ||
        typeof parsed.lngLat.lng !== 'number' ||
        !Number.isFinite(parsed.lngLat.lng) ||
        !('lat' in parsed.lngLat) ||
        typeof parsed.lngLat.lat !== 'number' ||
        !Number.isFinite(parsed.lngLat.lat)
    ) {
        return null;
    }

    const gameCoords = 'gameCoords' in parsed ? parsed.gameCoords : undefined;
    if (gameCoords !== undefined && gameCoords !== null && !isGameCoords(gameCoords)) {
        return null;
    }
    const gameCoordinateDeltas = 'gameCoordinateDeltas' in parsed ? parsed.gameCoordinateDeltas : undefined;
    if (
        gameCoordinateDeltas !== undefined &&
        gameCoordinateDeltas !== null &&
        !isGameCoordinateDeltas(gameCoordinateDeltas)
    ) {
        return null;
    }

    return {
        externalId: parsed.externalId,
        name: parsed.name,
        lngLat: { lng: parsed.lngLat.lng, lat: parsed.lngLat.lat },
        ...(gameCoords !== undefined ? { gameCoords } : {}),
        ...(gameCoordinateDeltas !== undefined ? { gameCoordinateDeltas } : {}),
    };
}

export interface PlayerPositionPayload {
    coords: MapDataGameCoordinates;
    bearing: number | null;
}

export const PLAYER_POSITION_LEASE_MS = 15_000;
export const MAX_MINI_MAP_REQUEST_ID_LENGTH = 100;

export function isPlayerPositionLeaseFresh(updatedAt: number | undefined, now = Date.now()): boolean {
    return updatedAt !== undefined && now - updatedAt < PLAYER_POSITION_LEASE_MS;
}

export function isMiniMapRequestId(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0 && value.length <= MAX_MINI_MAP_REQUEST_ID_LENGTH;
}

export type PreferencesClientMessage =
    | { type: 'preferences_update'; fields: PreferencesRecord }
    | { type: 'preferences_request' }
    | { type: 'trainer_presence'; connected: boolean; miniMapOpenHandler?: boolean }
    | ({ type: 'player_position' } & PlayerPositionPayload)
    | { type: 'player_position_stop' }
    | { type: 'mini_map_open_request'; requestId: string }
    | { type: 'mini_map_open_result'; requestId: string; success: boolean }
    | { type: 'fog_circles_add'; circles: Array<[number, number]> }
    | { type: 'fog_clear' };

export type PreferencesServerMessage =
    | { type: 'preferences_sync'; fields: PreferencesRecord }
    | { type: 'preferences_updated'; fields: PreferencesRecord }
    | { type: 'error'; message: string }
    | { type: 'trainer_presence'; connected: boolean }
    | ({ type: 'player_position' } & PlayerPositionPayload)
    | { type: 'player_position_stop' }
    | { type: 'mini_map_open_request'; requestId: string }
    | { type: 'mini_map_open_result'; requestId: string; success: boolean }
    | { type: 'fog_circles_hydrate'; circles: Array<[number, number]> }
    | { type: 'fog_circles_delta'; circles: Array<[number, number]> }
    | { type: 'fog_cleared' };

function isPreferencesRecord(value: unknown): value is PreferencesRecord {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;

    return Object.values(value).every(
        field =>
            typeof field === 'object' &&
            field !== null &&
            'value' in field &&
            typeof field.value === 'string' &&
            'updatedAt' in field &&
            typeof field.updatedAt === 'number' &&
            Number.isFinite(field.updatedAt),
    );
}

function isFogCircles(value: unknown): value is Array<[number, number]> {
    return (
        Array.isArray(value) &&
        value.every(
            circle =>
                Array.isArray(circle) &&
                circle.length === 2 &&
                typeof circle[0] === 'number' &&
                Number.isFinite(circle[0]) &&
                typeof circle[1] === 'number' &&
                Number.isFinite(circle[1]),
        )
    );
}

export function isPreferencesServerMessage(data: unknown): data is PreferencesServerMessage {
    if (typeof data !== 'object' || data === null || !('type' in data) || typeof data.type !== 'string') {
        return false;
    }

    switch (data.type) {
        case 'preferences_sync':
        case 'preferences_updated':
            return 'fields' in data && isPreferencesRecord(data.fields);
        case 'error':
            return 'message' in data && typeof data.message === 'string';
        case 'trainer_presence':
            return 'connected' in data && typeof data.connected === 'boolean';
        case 'player_position':
            return (
                'coords' in data &&
                isGameCoords(data.coords) &&
                'bearing' in data &&
                (data.bearing === null || (typeof data.bearing === 'number' && Number.isFinite(data.bearing)))
            );
        case 'fog_circles_hydrate':
        case 'fog_circles_delta':
            return 'circles' in data && isFogCircles(data.circles);
        case 'player_position_stop':
        case 'fog_cleared':
            return true;
        case 'mini_map_open_request':
            return 'requestId' in data && isMiniMapRequestId(data.requestId);
        case 'mini_map_open_result':
            return (
                'requestId' in data &&
                isMiniMapRequestId(data.requestId) &&
                'success' in data &&
                typeof data.success === 'boolean'
            );
        default:
            return false;
    }
}
